export type SharePlatform = 
  | 'copy' 
  | 'whatsapp' 
  | 'sms' 
  | 'twitter' 
  | 'facebook' 
  | 'linkedin'
  | 'image' 
  | 'native_share';

export interface ShareEvent {
  platform: SharePlatform;
  puzzleNumber: number;
  won: boolean;
  attempts: number;
  hadCaption: boolean;
  deviceType: string;
  timestamp: number;
}

const STORAGE_KEY = 'wordpop-share-analytics';
const MAX_EVENTS = 100;

// Device detection
function detectDeviceType(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Desktop (macOS)';
  if (/Windows/.test(ua)) return 'Desktop (Windows)';
  if (/Linux/.test(ua)) return 'Desktop (Linux)';
  return 'Unknown';
}

// Track share event
export function trackShareEvent(
  platform: SharePlatform,
  context: {
    puzzleNumber: number;
    won: boolean;
    attempts: number;
    hadCaption: boolean;
  }
): void {
  try {
    const newEvent: ShareEvent = {
      platform,
      puzzleNumber: context.puzzleNumber,
      won: context.won,
      attempts: context.attempts,
      hadCaption: context.hadCaption,
      deviceType: detectDeviceType(),
      timestamp: Date.now(),
    };

    const existingEvents = getShareEvents();
    const updatedEvents = [newEvent, ...existingEvents].slice(0, MAX_EVENTS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEvents));
    
    console.log(`[Share Analytics] ${platform} - Puzzle #${context.puzzleNumber} (${context.won ? 'Won' : 'Lost'} in ${context.attempts})`);
  } catch (error) {
    console.error('Failed to track share event:', error);
  }
}

// Get all share events
export function getShareEvents(): ShareEvent[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to retrieve share events:', error);
    return [];
  }
}

// Get aggregated share analytics
export interface ShareAnalyticsSummary {
  totalShares: number;
  platformBreakdown: Record<SharePlatform, number>;
  platformPercentages: Record<SharePlatform, number>;
  winShareRate: number;
  captionUsageRate: number;
  deviceBreakdown: Record<string, number>;
  topPlatform: SharePlatform | null;
  averageAttempts: number;
  recentEvents: ShareEvent[];
}

export function getShareAnalytics(): ShareAnalyticsSummary {
  const events = getShareEvents();
  
  if (events.length === 0) {
    return {
      totalShares: 0,
      platformBreakdown: {
        copy: 0,
        whatsapp: 0,
        sms: 0,
        twitter: 0,
        facebook: 0,
        linkedin: 0,
        image: 0,
        native_share: 0,
      },
      platformPercentages: {
        copy: 0,
        whatsapp: 0,
        sms: 0,
        twitter: 0,
        facebook: 0,
        linkedin: 0,
        image: 0,
        native_share: 0,
      },
      winShareRate: 0,
      captionUsageRate: 0,
      deviceBreakdown: {},
      topPlatform: null,
      averageAttempts: 0,
      recentEvents: [],
    };
  }
  
  // Platform breakdown
  const platformBreakdown: Record<SharePlatform, number> = {
    copy: 0,
    whatsapp: 0,
    sms: 0,
    twitter: 0,
    facebook: 0,
    linkedin: 0,
    image: 0,
    native_share: 0,
  };
  
  events.forEach(event => {
    platformBreakdown[event.platform] = (platformBreakdown[event.platform] || 0) + 1;
  });
  
  // Platform percentages
  const platformPercentages: Record<SharePlatform, number> = {} as Record<SharePlatform, number>;
  Object.entries(platformBreakdown).forEach(([platform, count]) => {
    platformPercentages[platform as SharePlatform] = (count / events.length) * 100;
  });
  
  // Top platform
  const topPlatform = Object.entries(platformBreakdown)
    .reduce((max, [platform, count]) => {
      return count > max.count ? { platform: platform as SharePlatform, count } : max;
    }, { platform: null as SharePlatform | null, count: 0 }).platform;
  
  // Win share rate
  const winShares = events.filter(e => e.won).length;
  const winShareRate = (winShares / events.length) * 100;
  
  // Caption usage rate
  const captionShares = events.filter(e => e.hadCaption).length;
  const captionUsageRate = (captionShares / events.length) * 100;
  
  // Device breakdown
  const deviceBreakdown: Record<string, number> = {};
  events.forEach(event => {
    deviceBreakdown[event.deviceType] = (deviceBreakdown[event.deviceType] || 0) + 1;
  });
  
  // Average attempts
  const totalAttempts = events.reduce((sum, e) => sum + e.attempts, 0);
  const averageAttempts = totalAttempts / events.length;
  
  return {
    totalShares: events.length,
    platformBreakdown,
    platformPercentages,
    winShareRate,
    captionUsageRate,
    deviceBreakdown,
    topPlatform,
    averageAttempts,
    recentEvents: events.slice(0, 10),
  };
}

// Clear share analytics
export function clearShareAnalytics(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[Share Analytics] Analytics cleared');
  } catch (error) {
    console.error('Failed to clear share analytics:', error);
  }
}
